import {
  Box,
  Flex,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  Tooltip
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FormFeedback, TagInput } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import {
  FormEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { fetchReviewerSearchAutoSuggestValues } from 'src/api/queries';
import {
  AUTOSUGGEST_DEBOUNCE_MS,
  escapeLuceneSpecialChars,
  MIN_AUTOSUGGEST_CHARACTERS
} from './AutoSuggest';

interface Props {
  value: string[];
  onChange: (nextValue: string[]) => void;
  ctxId: string;
  projectId: string;
  propertyId: string;
  fieldLabel?: string;
  isInvalid?: boolean;
  placeholder?: string;
  helperDisplay?: 'text' | 'tooltip';
  onDuplicateError?: (hasDuplicate: boolean) => void;
}

type TagFormValue = {
  tags: { text: string }[];
};

const normalizeValue = (item: string) => item.trim().toLowerCase();

const arraysMatch = (left: string[], right: string[]) =>
  left.length === right.length &&
  left.every((item, index) => item === right[index]);

const sanitizeValues = (items: string[]) => {
  return items.reduce<string[]>((acc, item) => {
    const trimmedItem = item.trim();
    acc.push(trimmedItem);
    return acc;
  }, []);
};

const toTagFormValue = (items: string[]) => ({
  tags: items.map(text => ({ text }))
});

const MultiSelectAutosuggest = ({
  value,
  onChange,
  ctxId,
  projectId,
  propertyId,
  fieldLabel,
  isInvalid,
  placeholder,
  helperDisplay = 'text',
  onDuplicateError
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const blurTimeoutRef = useRef<number | null>(null);
  const isOverLimitRef = useRef(false);
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const externalValue = useMemo(() => sanitizeValues(value), [value]);
  const previousExternalValueRef = useRef<string[]>(externalValue);

  const methods = useForm<TagFormValue>({
    defaultValues: toTagFormValue(externalValue)
  });

  const tagValues = useWatch({
    control: methods.control,
    name: 'tags'
  });

  const selectedValues = useMemo(
    () => sanitizeValues((tagValues ?? []).map(item => item?.text ?? '')),
    [tagValues]
  );

  const selectedValueSet = useMemo(
    () => new Set(selectedValues.map(item => normalizeValue(item))),
    [selectedValues]
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(inputValue.trim());
    }, AUTOSUGGEST_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [inputValue]);

  useEffect(() => {
    if (!arraysMatch(previousExternalValueRef.current, externalValue)) {
      previousExternalValueRef.current = externalValue;
      if (!isOverLimitRef.current) {
        methods.reset(toTagFormValue(externalValue));
      }
    }
  }, [externalValue, methods]);

  useEffect(() => {
    if (
      selectedValues.length <= 10 &&
      !arraysMatch(selectedValues, externalValue)
    ) {
      onChange(selectedValues);
    }
  }, [externalValue, onChange, selectedValues]);

  useEffect(() => {
    isOverLimitRef.current = selectedValues.length > 10;

    const rawNonEmpty = (tagValues ?? [])
      .map(item => normalizeValue(item?.text ?? ''))
      .filter(Boolean);
    const uniqueCount = new Set(rawNonEmpty).size;
    const hasDuplicates = rawNonEmpty.length > uniqueCount;

    if (hasDuplicates) {
      setDuplicateError(
        'Duplicate value added, clear the duplicates to continue.'
      );
    } else {
      setDuplicateError(null);
    }

    onDuplicateError?.(hasDuplicates || isOverLimitRef.current);
  }, [tagValues, onDuplicateError, selectedValues]);

  useEffect(
    () => () => {
      if (blurTimeoutRef.current) {
        window.clearTimeout(blurTimeoutRef.current);
      }
    },
    []
  );

  const trimmedInputValue = inputValue.trim();
  const hasMetMinimumLength =
    debouncedValue.length >= MIN_AUTOSUGGEST_CHARACTERS;
  const helperText = `Type and select multiple ${
    fieldLabel ?? 'values'
  } to find relevant reviewers faster`;

  const { data, isFetching } = useQuery({
    queryKey: ['reviewer-autosuggest-multi', propertyId, debouncedValue],
    queryFn: () =>
      fetchReviewerSearchAutoSuggestValues({
        ctxId,
        projectId,
        schemaID: propertyId,
        value: escapeLuceneSpecialChars(debouncedValue)
      }),
    enabled: !!propertyId && hasMetMinimumLength
  });

  const suggestions = useMemo(
    () =>
      (data?.items ?? []).filter(
        item => !selectedValueSet.has(normalizeValue(item))
      ),
    [data?.items, selectedValueSet]
  );

  const getTypingInput = () =>
    containerRef.current?.querySelector('input') ?? null;

  const syncInputElement = (nextValue: string) => {
    const typingInput = getTypingInput();

    if (typingInput) {
      typingInput.value = nextValue;
    }
  };

  const focusTypingInput = () => {
    const typingInput = getTypingInput();

    if (typingInput) {
      typingInput.focus();
    }
  };

  const handleSelect = (item: string) => {
    const nextValues = sanitizeValues([...selectedValues, item]);

    methods.reset(toTagFormValue(nextValues));
    onChange(nextValues);
    setInputValue('');
    setDebouncedValue('');
    syncInputElement('');
    setIsOpen(false);
    focusTypingInput();
  };

  const handleInputCapture = (event: FormEvent<HTMLDivElement>) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    if (selectedValues.length >= 10) {
      setIsOpen(false);
      return;
    }

    setInputValue(target.value);
    setIsOpen(target.value.trim().length > 0);
    setDuplicateError(null);
  };

  const handleFocusCapture = () => {
    if (blurTimeoutRef.current) {
      window.clearTimeout(blurTimeoutRef.current);
    }

    setIsOpen(trimmedInputValue.length > 0);
  };

  const handleBlurCapture = () => {
    blurTimeoutRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    if (event.key === 'Enter' && target.value.trim().length === 0) {
      event.preventDefault();
      event.stopPropagation();
      target.value = '';
      setInputValue('');
    }

    if (event.key === 'Enter' && selectedValues.length >= 10) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (event.key === 'Backspace' && target.value === '') {
      setInputValue('');
      setDebouncedValue('');
    }
  };

  return (
    <Box width="100%">
      <Flex alignItems="flex-start" gap={2} width="100%">
        <Box flex={1} minW={0}>
          <Popover
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            placement="bottom-start"
            matchWidth
            closeOnBlur={false}
            autoFocus={false}
          >
            <PopoverTrigger>
              <Box
                width="100%"
                minW={0}
                ref={containerRef}
                onInputCapture={handleInputCapture}
                onFocusCapture={handleFocusCapture}
                onBlurCapture={handleBlurCapture}
                onKeyDownCapture={handleKeyDownCapture}
                sx={{
                  // TagInput reserves a second row for helper/count content.
                  // This component renders validation outside, so collapsing that
                  // internal row keeps the field aligned without affecting the menu.
                  '& > div': {
                    gap: 0
                  },
                  '& > div > div:last-child': {
                    display: 'none'
                  }
                }}
              >
                <TagInput
                  control={methods.control}
                  name="tags"
                  isInvalid={isInvalid}
                  placeholder={
                    placeholder ??
                    `Type at least ${MIN_AUTOSUGGEST_CHARACTERS} characters and press Enter to search`
                  }
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    '& span': {
                      whiteSpace: 'normal !important',
                      wordBreak: 'break-word'
                    }
                  }}
                />
              </Box>
            </PopoverTrigger>

            <PopoverContent
              minW="100%"
              zIndex={9999}
              bg="white"
              border="1px solid"
              borderColor="neutralPrimaryBorder"
              borderRadius="md"
              boxShadow="md"
              maxH="200px"
              overflowY="auto"
            >
              <PopoverBody padding={0}>
              {!trimmedInputValue ? (
                <Box px={3} py={2}>
                  <Text>
                    Type at least {MIN_AUTOSUGGEST_CHARACTERS} characters to
                    search.
                  </Text>
                </Box>
              ) : trimmedInputValue.length < MIN_AUTOSUGGEST_CHARACTERS ? (
                <Box px={3} py={2}>
                  <Text>
                    Type at least {MIN_AUTOSUGGEST_CHARACTERS} characters to see
                    suggestions.
                  </Text>
                </Box>
              ) : isFetching ? (
                <Box p={2} textAlign="center">
                  <Spinner size="sm" />
                </Box>
              ) : (
                <List>
                  {suggestions.map(item => (
                    <ListItem
                      key={item}
                      px={3}
                      py={2}
                      _hover={{ bg: 'actionSecondaryFill', cursor: 'pointer' }}
                      onMouseDown={() => handleSelect(item)}
                    >
                      <Text>{item}</Text>
                    </ListItem>
                  ))}
                  {suggestions.length === 0 && (
                    <Box px={3} py={2}>
                      <Text>No matches found.</Text>
                    </Box>
                  )}
                </List>
              )}
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Box>

        {helperDisplay === 'tooltip' && (
          <Tooltip hasArrow placement="top" label={helperText}>
            <Box
              color="neutralSecondaryContent"
              cursor="pointer"
              lineHeight={1}
              aria-label={helperText}
              paddingTop={3}
              paddingX={1}
              flexShrink={0}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </Box>
          </Tooltip>
        )}

        {helperDisplay === 'text' && (
          <Text mt={1} textStyle="textContext" color="neutralSecondaryContent">
            {helperText}
          </Text>
        )}
      </Flex>

      {selectedValues.length > 10 && (
        <FormFeedback variant="error">Maximum of 10 tags reached.</FormFeedback>
      )}

      {duplicateError && (
        <FormFeedback variant="error">{duplicateError}</FormFeedback>
      )}
    </Box>
  );
};

export default MultiSelectAutosuggest;
